/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useState, useEffect, useContext, useRef } from 'react';
import DOMPurify from 'dompurify';

// Enforce http/https-only on every src and href that DOMPurify keeps.
// This runs once when the module is first loaded.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // Block javascript: and other dangerous protocols, but allow:
  //   - http:// / https://  (absolute URLs)
  //   - data:image/...      (base64 embedded images)
  //   - /path or ./path     (relative URLs served from the same origin)
  //   - blob:               (object URLs created by the browser)
  const ALLOWED_PROTO = /^(https?:\/\/|\/|\.{1,2}\/|blob:|data:image\/)/i;
  const urlAttrs = ['src', 'href'] as const;
  urlAttrs.forEach((attr) => {
    const value = (node as Element).getAttribute?.(attr);
    if (value && !ALLOWED_PROTO.test(value)) {
      (node as Element).removeAttribute(attr);
    }
  });
});
import {
  Box,
  Button,
  Alert,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  SvgIcon,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { ArrowLeft } from '../../../assets';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  TrainingQuizQuestion,
} from '../../../pages/Compliance/Interfaces/complicanceInterfaces';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { trainingClient, TrainingModuleDetailDto } from '../../../app/services/api/compliance/training';
import { UserContext } from '../../../components/shared/useUser';
import useLogError from '../../../hooks/useLogError';
import { Section } from '../../../components/ui/Section';

interface QuizAnswers {
  [questionId: number]: {
    answer: string | string[];
    score: number;
  };
}

const TakeTrainingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation('training');
  const { moduleId } = useParams<{ moduleId: string }>();
  const { user } = useContext(UserContext);
  const { logError } = useLogError();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  const topAnchorRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [module, setModule] = useState<TrainingModuleDetailDto | null>(null);
  const [assignmentId, setAssignmentId] = useState<number | null>(null);
  const [currentStep, setCurrentStep] = useState(0); // 0-n for content, n+1 for quiz
  const [quizAnswers, setQuizAnswers] = useState<QuizAnswers>({});
  const [quizResults, setQuizResults] = useState<{ [quizIndex: number]: { score: number; maxScore: number; submitted: boolean } }>({});
  const [resultDialogOpen, setResultDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Cache of fully-loaded section HTML keyed by section id
  const [loadedSections, setLoadedSections] = useState<Map<number, string | null>>(new Map());

  const quizzes = module?.quizzes ?? [];
  // Sort content by displayOrder so sections always appear 1 → last
  const contentSteps = [...(module?.content ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);
  const totalSteps = contentSteps.length + quizzes.length;
  const isOnQuiz = currentStep >= contentSteps.length;
  const currentContent = !isOnQuiz ? contentSteps[currentStep] : undefined;
  const currentQuizIndex = isOnQuiz ? currentStep - contentSteps.length : -1;
  const currentQuiz = currentQuizIndex >= 0 ? quizzes[currentQuizIndex] : undefined;
  const isLastQuizStep = isOnQuiz && currentStep === totalSteps - 1;
  const isAllSubmitted = quizzes.length > 0 && quizzes.every((_, i) => quizResults[i]?.submitted ?? false);
  const { selectedStore, selectedFranchise } = useContext(UserContext);

  useEffect(() => {
    loadModule();
  }, [moduleId]);

  // Scroll to top on step change.
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      (document.activeElement as HTMLElement)?.blur();
      topAnchorRef.current?.scrollIntoView();
    });
    return () => cancelAnimationFrame(id);
  }, [currentStep]);

  useEffect(() => {
    if (!sectionLoading) {
      topAnchorRef.current?.scrollIntoView();
    }
  }, [sectionLoading]);

  useEffect(() => {
    if (!module || isOnQuiz || !currentContent) return;
    const sectionId = currentContent.id;
    if (!sectionId || loadedSections.has(sectionId)) return;

    const fetchSection = async () => {
      try {
        setSectionLoading(true);
        const detail = await trainingClient.getSectionById(Number(moduleId), sectionId);
        setLoadedSections(prev => new Map(prev).set(sectionId, detail?.contentText ?? null));
      } catch (err) {
        logError(err);
        setLoadedSections(prev => new Map(prev).set(sectionId, currentContent.contentText ?? null));
      } finally {
        setSectionLoading(false);
      }
    };
    fetchSection();
  }, [currentStep, module]);

  const loadModule = async () => {
    if (!moduleId || !user?.id) return;

    try {
      setLoading(true);
      const data = await trainingClient.getModule(parseInt(moduleId));
      setModule(data);

      // Get the user's assignment for this module
      const assignments = await trainingClient.getCompletionsByUserId(user.id);
      const currentAssignment = assignments.find(a => a.moduleId === parseInt(moduleId));
      if (currentAssignment) {
        if (currentAssignment.status === 'Completed') {
          navigate('/compliance/training/my-training');
          return;
        }
        setAssignmentId(currentAssignment.id);
      } else {
        setError(t('takeTraining.noAssignmentFound'));
      }
    } catch (err) {
      logError(err);
      setError(err instanceof Error ? err.message : t('takeTraining.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (module) {
      const total = (module.content?.length || 0) + (module.quizzes?.length || 0);
      if (currentStep < total - 1) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleQuizAnswer = (questionId: number, question: TrainingQuizQuestion, answer: string | string[]) => {
    // Calculate score for this answer
    let score = 0;

    // Always treat answer as an array since we use checkboxes for everything
    const selectedAnswers = Array.isArray(answer) ? answer : [answer];
    const correctAnswers = question.answers?.filter(ans => ans.isCorrect) || [];
    const correctAnswerTexts = correctAnswers.map(ans => ans.answerText);

    // Check if the selected answers match exactly with correct answers
    const selectedCorrectCount = selectedAnswers.filter(ans => correctAnswerTexts.includes(ans)).length;
    const selectedIncorrectCount = selectedAnswers.length - selectedCorrectCount;

    // Award full points only if:
    // 1. All correct answers are selected
    // 2. No incorrect answers are selected
    if (selectedCorrectCount === correctAnswers.length && selectedIncorrectCount === 0) {
      score = question.points || 0;
    }

    setQuizAnswers({
      ...quizAnswers,
      [questionId]: { answer: selectedAnswers, score },
    });
  };

  const handleSubmitQuiz = async () => {
    if (!assignmentId || !quizzes.length) {
      setError(t('takeTraining.cannotSubmit'));
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const newResults: { [key: number]: { score: number; maxScore: number; submitted: boolean } } = {};

      for (let quizIdx = 0; quizIdx < quizzes.length; quizIdx++) {
        const quiz = quizzes[quizIdx];
        const maxScore = quiz.questions?.reduce((sum: number, q: { points: number }) => sum + (q.points || 0), 0) || 0;

        const answers = (quiz.questions || []).map(question => {
          const answerData = quizAnswers[question.id];
          if (!answerData) return null;
          const selectedAnswerTexts = Array.isArray(answerData.answer) ? answerData.answer : [answerData.answer];
          const selectedAnswerIds = question.answers
            ?.filter(ans => selectedAnswerTexts.includes(ans.answerText))
            .map(ans => ans.id) || [];
          return {
            QuestionId: question.id,
            SelectedAnswers: selectedAnswerIds,
          };
        }).filter(a => a !== null);

        const result = await trainingClient.submitQuiz({
          assignmentId,
          answers: answers as any,
        });

        newResults[quizIdx] = { score: result.score, maxScore, submitted: true };
      }

      setQuizResults(prev => ({ ...prev, ...newResults }));
      setResultDialogOpen(true);

    } catch (err) {
      logError(err);
      setError(err instanceof Error ? err.message : t('takeTraining.failedToSubmit'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinish = () => {
    // Add timestamp to force refresh of My Training page data
    navigate('/compliance/training/my-training?refresh=' + Date.now());
  };

  if (loading) {
    return (
      <PageArea>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  if (!module) {
    return (
      <PageArea>
        <Alert severity="error">{t('takeTraining.moduleNotFound')}</Alert>
      </PageArea>
    );
  }

  

  // Overall score across all submitted quizzes
  const totalScore = Object.values(quizResults).reduce((sum, r) => sum + r.score, 0);
  const totalMaxScore = Object.values(quizResults).reduce((sum, r) => sum + r.maxScore, 0);
  const overallPercentage = totalMaxScore > 0 ? (totalScore / totalMaxScore) * 100 : 0;
  const passed = isAllSubmitted && quizzes.every((q, i) => {
    const result = quizResults[i];
    if (!result) return false;
    const pct = result.maxScore > 0 ? (result.score / result.maxScore) * 100 : 0;
    return pct >= (q.passingScore ?? 70);
  });

  // Check ALL quiz questions across ALL quizzes are answered (for final Submit button)
  const allQuizQuestionsAnswered = quizzes.every(quiz =>
    (quiz.questions || []).every(q => {
      const answer = quizAnswers[q.id]?.answer;
      return Array.isArray(answer) ? answer.length > 0 : answer !== undefined && answer !== null;
    }),
  );

  // For per-quiz unanswered warning on the current quiz step
  const currentQuizTotalQ = currentQuiz?.questions?.length || 0;
  const currentQuizAnsweredQ = currentQuiz?.questions?.filter(q => {
    const answer = quizAnswers[q.id]?.answer;
    return Array.isArray(answer) ? answer.length > 0 : answer !== undefined && answer !== null;
  }).length || 0;

  return (
    <PageArea>
      <div ref={topAnchorRef} />
      {/* Header */}
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between', mb: 3 }}
      >
        <PageTitle subTitle={`${module.categoryName} • ${module.estimatedDurationMinutes} ${t('takeTraining.minutes')}`}>
          {module.name}
        </PageTitle>
        <Button
          variant="secondary"
          size="lg"
          startIcon={<SvgIcon><ArrowLeft /></SvgIcon>}
          onClick={() => navigate('/compliance/training/my-training')}
        >
          {t('takeTraining.backToMyTraining')}
        </Button>
      </Stack>

      {error && (
        <Alert severity="error"
          onClose={() => setError(null)}
          sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Progress Stepper */}
      <Box sx={{ mt: 10, mb: 10 }}>
        <Stepper activeStep={currentStep}
          alternativeLabel>
          {contentSteps.map((content, index) => (
            <Step key={content.id}>
              <StepLabel>{content.title || `${t('takeTraining.lesson')} ${index + 1}`}</StepLabel>
            </Step>
          ))}
          {quizzes.map((q, index) => (
            <Step key={`quiz-${q.id ?? index}`}>
              <StepLabel>{q.title || `${t('takeTraining.quiz')} ${quizzes.length > 1 ? index + 1 : ''}`}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {/* Content Display */}
      {!isOnQuiz && currentContent && (
        <Section style={{ p: 5, mb: 5 }}>
          <Box sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 2 }}>
            {currentContent.title}
          </Box>
          {currentContent.estimatedMinutes && (
            <Box
              sx={{
                fontSize: '0.875rem',
                color: theme.palette.custom.gray[600],
                mb: 2,
                fontStyle: 'italic',
              }}
            >
              {t('takeTraining.estimatedTime')}: {currentContent.estimatedMinutes} {t('takeTraining.minutes')}
            </Box>
          )}
          {sectionLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                fontSize: '1rem',
                lineHeight: 1.8,
                color: theme.palette.text.primary,
                maxWidth: '100%',
                overflowX: 'hidden',
                '& p': {
                  marginBottom: '1em',
                  overflowWrap: 'anywhere',
                },
                '& ul, & ol': {
                  marginLeft: '1.5em',
                  marginBottom: '1em',
                },
                '& li': {
                  marginBottom: '0.5em',
                  overflowWrap: 'anywhere',
                },
                '& strong': {
                  fontWeight: 600,
                },
                '& em': {
                  fontStyle: 'italic',
                },
                [`@media (max-width:${theme.breakpoints.values.md}px)`]: {
                  '& img': {
                    display: 'block',
                    maxWidth: '100% !important',
                    width: 'auto',
                    height: 'auto !important',
                    margin: '8px auto',
                  },
                },
                '& video': {
                  width: '100%',
                  maxWidth: '640px',
                  aspectRatio: '16/9',
                  display: 'block',
                  margin: '8px auto',
                  background: '#000',
                },
              }}
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  loadedSections.has(currentContent.id)
                    ? (loadedSections.get(currentContent.id) ?? `<p>${t('takeTraining.contentNotAvailable')}</p>`)
                    : (currentContent.contentText || `<p>${t('takeTraining.contentNotAvailable')}</p>`),
                  {
                    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'a', 'img', 'video', 'source'],
                    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'style', 'class', 'controls', 'type', 'preload'],
                  },
                ),
              }}
            />
          )}
        </Section>
      )}

      {/* Quiz Display */}
      {isOnQuiz && currentQuiz && !isAllSubmitted && (
        <Section style={{ p: 5, mb: 5 }}>

          <Box sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 1 }}>
            {t('takeTraining.quizTitle')}: {currentQuiz.title}
          </Box>
          {currentQuiz.description && (
            <Box
              sx={{
                fontSize: '0.875rem',
                color: theme.palette.custom.gray[600],
                mb: 3,
              }}
            >
              {currentQuiz.description}
            </Box>
          )}

          {currentQuiz.questions?.map((question, qIndex) => (
            <Section
              key={question.id}
              style={{
                p: 5,
                mb: 3,
                boxShadow: 'none',
              }}
            >
              <Box sx={{ fontSize: '1rem', fontWeight: 500, mb: 2 }}>
                {qIndex + 1}. {question.questionText}
                {question.points !== undefined && question.points !== null && question.points > 0 && (
                  <Box
                    component="span"
                    sx={{
                      ml: 2,
                      fontSize: '0.875rem',
                      color: theme.palette.custom.gray[600],
                      fontWeight: 400,
                    }}
                  >
                    ({question.points} {question.points === 1 ? t('takeTraining.point') : t('takeTraining.points')})
                  </Box>
                )}
              </Box>

              {/* Simplified checkbox-based answer selection */}
              <FormGroup>
                {question.answers?.map((answer) => (
                  <FormControlLabel
                    key={answer.id}
                    control={
                      <Checkbox
                        checked={(quizAnswers[question.id]?.answer as string[] || []).includes(answer.answerText)}
                        onChange={(e) => {
                          const currentAnswers = (quizAnswers[question.id]?.answer as string[]) || [];
                          const newAnswers = e.target.checked
                            ? [...currentAnswers, answer.answerText]
                            : currentAnswers.filter(a => a !== answer.answerText);
                          handleQuizAnswer(question.id, question, newAnswers);
                        }}
                      />
                    }
                    label={answer.answerText}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.95rem',
                      },
                    }}
                  />
                ))}
              </FormGroup>
            </Section>
          ))}
        </Section>
      )}

      {/* Unanswered questions warning - shown per quiz step so user knows what's left */}
      {isOnQuiz && !isAllSubmitted && currentQuizAnsweredQ < currentQuizTotalQ && (
        <Alert severity="info"
          sx={{ mb: 3 }}>
          {t('takeTraining.unansweredWarning', { total: currentQuizTotalQ, answered: currentQuizAnsweredQ })}
        </Alert>
      )}

      {/* All-quiz unanswered warning - only on last quiz step before submit */}
      {isLastQuizStep && !isAllSubmitted && !allQuizQuestionsAnswered && currentQuizAnsweredQ === currentQuizTotalQ && (
        <Alert severity="warning"
          sx={{ mb: 3 }}>
          {t('takeTraining.unansweredWarningAllQuizzes')}
        </Alert>
      )}

      {/* Quiz Results */}
      {isLastQuizStep && isAllSubmitted && (
        <Section style={{ p: 5, mb: 5 }}>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <SvgIcon sx={{ fontSize: 80, color: passed ? 'success.main' : 'error.main', mb: 2 }}>
              {passed ? <CheckCircleIcon /> : <CancelIcon />}
            </SvgIcon>
            <Box sx={{ fontSize: '1.5rem', fontWeight: 600, mb: 1 }}>
              {passed ? t('takeTraining.congratulations') : t('takeTraining.quizNotPassed')}
            </Box>
            <Box sx={{ fontSize: '1rem', color: theme.palette.custom.gray[600], mb: 3 }}>
              {t('takeTraining.yourScore')}: {totalScore} / {totalMaxScore} ({overallPercentage.toFixed(1)}%)
            </Box>
            {passed && (
              <Box sx={{ fontSize: '1rem', color: 'success.main', mb: 2 }}>
                {t('takeTraining.certificateGenerated')}
              </Box>
            )}
            <LinearProgress
              variant="determinate"
              value={overallPercentage}
              color={passed ? 'success' : 'error'}
              sx={{ height: 10, borderRadius: 5, mb: 3 }}
            />
          </Box>
        </Section>
      )}

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="secondary"
          size="lg"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          disabled={currentStep === 0}
        >
          {t('takeTraining.previous')}
        </Button>

        {/* Next: all steps except the last, and not yet submitted */}
        {currentStep < totalSteps - 1 && !isAllSubmitted && (
          <Button
            variant="primary"
            size="lg"
            endIcon={<ArrowForwardIcon />}
            onClick={handleNext}
          >
            {t('takeTraining.next')}
          </Button>
        )}

        {/* Submit: only on the last quiz step, before submission */}
        {isLastQuizStep && !isAllSubmitted && (
          <Button
            variant="primary"
            size="lg"
            onClick={handleSubmitQuiz}
            disabled={!allQuizQuestionsAnswered || submitting}
          >
            {submitting ? t('takeTraining.submitting') : t('takeTraining.submitQuiz')}
          </Button>
        )}

        {/* Finish: after all quizzes submitted */}
        {isAllSubmitted && (
          <Button
            variant="primary"
            size="lg"
            onClick={handleFinish}
          >
            {t('takeTraining.finish')}
          </Button>
        )}
      </Box>

      {/* Result Dialog */}
      <Dialog
        open={resultDialogOpen}
        onClose={() => setResultDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {passed ? t('takeTraining.trainingCompleted') : t('takeTraining.quizResults')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <SvgIcon sx={{ fontSize: 60, color: passed ? 'success.main' : 'error.main', mb: 2 }}>
              {passed ? <CheckCircleIcon /> : <CancelIcon />}
            </SvgIcon>
            <Box sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 1 }}>
              {t('takeTraining.yourScore')}: {totalScore} / {totalMaxScore}
            </Box>
            <Box sx={{ fontSize: '1rem', mb: 2 }}>
              {overallPercentage.toFixed(1)}%
            </Box>
            {passed ? (
              <Box sx={{ fontSize: '0.875rem', color: 'success.main' }}>
                {t('takeTraining.completedSuccessfully')}
              </Box>
            ) : (
              <Box sx={{ fontSize: '0.875rem', color: 'error.main' }}>
                {t('takeTraining.tryAgainLater', { passingScore: Math.min(...quizzes.map(q => q.passingScore ?? 70)) })}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="primary"
            size="lg"
            onClick={() => setResultDialogOpen(false)}>
            {t('takeTraining.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </PageArea>
  );
};

export default TakeTrainingPage;
